import { call, put, race, take, takeEvery, takeLatest, takeLeading } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';
import { getEnvironmentConfig, getLogger, httpClientInterface } from '../../utils/core';
import { categoriesActions } from '../categories';
import { resourceSagaUtils, resourceStoreTypes } from '../core';
import { filtersActions } from '../filters';
import { tagsActions } from '../tags';
import { transactionsActions } from '../transactions';

import * as actions from './spendingWatchlistActions';
import { getLastSyncDate } from './spendingWatchlistSelectors';
import {
  transformResponseToSpendingWatchLists,
  transformSpendingWatchListToRequestData,
} from './spendingWatchlistTransformers';
import { mkSpendingWatchList } from './spendingWatchlistTypes';

const log = getLogger('data/spendingWatchList/sagas');

function* createWatchListWorker(action) {
  const title = action.payload.get('title');
  const targetAmt = action.payload.get('targetAmount');
  const emoji = action.payload.get('emoji');
  const period = action.payload.get('period');
  const startDate = action.payload.get('startDate');
  const endDate = action.payload.get('endDate');

  yield put(filtersActions.createFilter(action.payload.get('filterItems')));

  const result = yield race({
    filterSuccess: take(filtersActions.createFilterSuccess),
    filterFailure: take(filtersActions.createFilterFailure),
  });
  const filterId = result?.filterSuccess?.payload?.id || '';

  // Attached returned filter Id to spending watch list
  const newWatchList = mkSpendingWatchList({
    id: null,
    clientId: uuidv4().toUpperCase(),
    name: title,
    isDeleted: null,
    filterId,
    targetAmount: targetAmt,
    emoji,
    period,
    startDate,
    endDate,
  });

  yield put(actions.createSpendingWatchList(newWatchList));
}

function* deleteWatchListWorker(action) {
  const { payload: resource } = action;

  if (resource.filterToDelete) {
    yield put(filtersActions.deleteFilter(resource.filterToDelete));
  }
  yield put(actions.deleteSpendingWatchList(resource.spendingWatchListToDelete));
}
// ====================================================
// ACTION WATCHERS to trigger SAGAS calls

export function* createWatchListActionWatcher() {
  yield takeLatest(actions.createWatchList, createWatchListWorker);
}

export function* deleteWatchListActionWatcher() {
  yield takeEvery(actions.deleteWatchList, deleteWatchListWorker);
}

const mkResourceConfig = (successAction, failureAction) =>
  resourceStoreTypes.mkQcsSyncResourceConfig({
    resourceName: 'spendingWatchList',
    resourceBaseUrl: '/spending-watchlist',
    getLastSyncDate,

    transformResponseToResources: transformResponseToSpendingWatchLists,
    transformResourceToRequestData: transformSpendingWatchListToRequestData,

    successAction,
    failureAction,
  });

function* getSpendingWatchlistNoCalc(action) {
  yield call(
    resourceSagaUtils.qcsSyncGetResources,
    mkResourceConfig(actions.getSpendingWatchListsSuccess, actions.getSpendingWatchListsFailure),
    action,
  );
}

function* getSpendingWatchListCalc(action) {
  try {
    log.log('getSpendingWatchListAction: server calculation start...');

    const response = yield call(
      httpClientInterface.send,
      'GET',
      `${getEnvironmentConfig().services_url}/spending-watchlist/calc`,
    );

    log.log('getSpendingWatchListAction: server calculation complete...\nresponse = ', response);
  } catch (error) {
    log.log('getSpendingWatchListAction: server calculation failed...\nerror = ', error);
  }

  yield call(
    resourceSagaUtils.qcsSyncGetResources,
    mkResourceConfig(actions.getSpendingWatchListsSuccess, actions.getSpendingWatchListsFailure),
    action,
  );
}

export function* getSpendingWatchListInitialActionWatcher() {
  yield takeLeading(actions.getSpendingWatchListsInitial, getSpendingWatchlistNoCalc);
}

export function* getSpendingWatchListActionWatcher() {
  yield takeLeading(actions.getSpendingWatchLists, getSpendingWatchListCalc);
}

export function* createSpendingWatchListActionWatcher() {
  const resourceConfig = mkResourceConfig(
    actions.createSpendingWatchListSuccess,
    actions.createSpendingWatchListFailure,
  );
  yield takeEvery(actions.createSpendingWatchList, resourceSagaUtils.qcsSyncCreateResource, resourceConfig);
}

export function* updateSpendingWatchListActionWatcher() {
  const resourceConfig = mkResourceConfig(
    actions.updateSpendingWatchListSuccess,
    actions.updateSpendingWatchListFailure,
  );
  yield takeEvery(actions.updateSpendingWatchList, resourceSagaUtils.qcsSyncUpdateResource, resourceConfig);
}

export function* deleteSpendingWatchListActionWatcher() {
  const resourceConfig = mkResourceConfig(
    actions.deleteSpendingWatchListSuccess,
    actions.deleteSpendingWatchListFailure,
  );
  yield takeEvery(actions.deleteSpendingWatchList, resourceSagaUtils.qcsSyncDeleteResource, resourceConfig);
}

function* spendingWatchListCalc() {
  yield put(filtersActions.getFilters());
  yield put(actions.getSpendingWatchLists());
}

function* spendingWatchListCalcTriggersWatcher() {
  yield takeEvery(
    [
      actions.createSpendingWatchListSuccess,
      actions.updateSpendingWatchListSuccess,
      actions.deleteSpendingWatchListSuccess,
      transactionsActions.createTransactionSuccess,
      transactionsActions.batchTransactionsSuccess,
      transactionsActions.updateTransactionSuccess,
      transactionsActions.deleteTransactionSuccess,
      categoriesActions.createCategorySuccess,
      tagsActions.deleteTag,
    ],
    function* spendingWatchListCalcHandler(action) {
      let doCalc;
      switch (action.type) {
        case String(transactionsActions.createTransactionSuccess):
        case String(transactionsActions.batchTransactionsSuccess):
        case String(transactionsActions.updateTransactionSuccess):
        case String(transactionsActions.deleteTransactionSuccess):
          if (action.meta?.context === 'Reg_SpendingWatchListGraph') {
            doCalc = true;
          }
          break;
        default:
          doCalc = true;
      }
      if (doCalc) {
        yield call(spendingWatchListCalc);
      }
    },
  );
}

export default [
  createWatchListActionWatcher,
  deleteWatchListActionWatcher,
  getSpendingWatchListInitialActionWatcher,
  getSpendingWatchListActionWatcher,
  createSpendingWatchListActionWatcher,
  updateSpendingWatchListActionWatcher,
  deleteSpendingWatchListActionWatcher,
  spendingWatchListCalcTriggersWatcher,
];
